import * as React from "react";
import cx from "classnames";

import { ControlProps } from "react-select/lib/components/Control";
import { MenuListComponentProps } from "react-select/lib/components/Menu";
import { Button } from "@common/components/Button";
import { ClickAwayListener } from "@common/components/ClickAwayListener";
import { Flex } from "@common/components/Flex";
import { FormatFieldSelector } from "@common/components/format-field-selector";
import { FormatColumnKey } from "@common/components/format-field-selector/components/formats/formats.types";
import { useMessage } from "@common/hooks/message";
import {
  DraggableList,
  DraggableListDataItem,
  DraggableListDirection,
  DraggableListProps,
} from "@common/components/draggable-list";
import Dropdown, { components, DROPDOWN_NO_VALUE } from "@common/containers/fields/Dropdown";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { DATASHEETS_MODULE_NAME } from "@modules/datasheets/constants/common";
import { GetDatasheetFolderFormats } from "@modules/datasheets/services/formats";
import { trDts } from "@modules/datasheets/utils/i18n";
import { FormatFieldItem } from "@common/components/format-field-selector";

import "./tabs.styles.scss";
import {
  DatasheetFormatManagementFolder,
  useDatasheetFormatManagementController,
} from "@modules/datasheets/components/result/components/header/format-management.controller";

export type TabsItem = {
  id: number | string;
  name: string;
};

export type TabsItemOnOrderChange = (tabs: TabsItem[]) => void;

export type TabsFormatManagementProps = {
  getFoldersForDatasheet: () => Promise<DatasheetFormatManagementFolder[]>;
  getDatasheetFolderFormats: (params: GetDatasheetFolderFormats) => Promise<Array<FormatFieldItem>>;
  onDatasheetFormatsSubmit: (params: { format: FormatFieldItem; folder: DatasheetFormatManagementFolder }) => void;
  forceOpenFormatPicker?: boolean;
  onFormatPickerClose?: () => void;
  onSetFolderDefaultFormat?: (params: { folderId: number; formatId: number }) => void;
  onSetPedigreeDefaultFormat?: (formatId: number) => void;
  canSetFolderDefault?: boolean;
  context?: number;
  templateOptionOn?: boolean;
  /**
   * When true, the parent Result header is in "format only" mode (pedigree
   * split view). In that mode, even for IDS context, the label should say
   * "this Pedigree" instead of "this Individual".
   */
  formatOnly?: boolean;
  /** IDS/PDS folder id; used as folder target when templateOptionOn and selectedFolder is not set */
  rootFolderId?: number;
  /** Current format loaded for the pedigree/individual. Shown on the "current" line (GWT-style). */
  initialSelectedFormat?: FormatFieldItem | { id: number; name: string; folderId?: number };
  /** When changed (e.g. pedigree reopened), reload recently used formats from backend */
  reloadRecentlyUsedKey?: number | string;
  /** When true (e.g. another user holds the datasheet lock), block opening the format picker. */
  interactionLocked?: boolean;
};

export type TabsProps = {
  className?: string;
  items: Array<TabsItem>;
  onChange: (tabId: TabsItem["id"]) => void;
  onTabOrderChange: TabsItemOnOrderChange;
  value: TabsItem["id"];
  classes?: {
    tabs?: string;
  };
  formatManagement?: TabsFormatManagementProps;
};

const FormatManagementDropdown: React.FC<TabsFormatManagementProps> = (props) => {
  const { showErrorMessage } = useMessage();
  const [datasheetFormatsManagementOpened, setDatasheetFormatsManagementOpened] = React.useState(false);

  // Open when parent asks (e.g. "no default format" → Yes). Do not require !interactionLocked:
  // until loadDSFormat completes, datasheetEditLocked can be true while the user still must pick a format.
  React.useEffect(() => {
    if (props.forceOpenFormatPicker) {
      setDatasheetFormatsManagementOpened(true);
    }
  }, [props.forceOpenFormatPicker]);

  const interactionLockedPrevRef = React.useRef(false);
  React.useEffect(() => {
    const locked = Boolean(props.interactionLocked);
    const becameLocked = locked && !interactionLockedPrevRef.current;
    interactionLockedPrevRef.current = locked;
    if (!becameLocked) {
      return;
    }
    // Parent-driven "pick a format" flow must stay open even if interactionLocked is already true.
    if (props.forceOpenFormatPicker) {
      return;
    }
    setDatasheetFormatsManagementOpened(false);
  }, [props.interactionLocked, props.forceOpenFormatPicker]);

  const handleDatasheetFormatsManagementOpen = React.useCallback(() => {
    if (props.interactionLocked) {
      return;
    }
    setDatasheetFormatsManagementOpened(true);
  }, [props.interactionLocked]);

  const handleDatasheetFormatsManagementClose = React.useCallback(() => {
    setDatasheetFormatsManagementOpened(false);
    if (props.forceOpenFormatPicker && props.onFormatPickerClose) {
      props.onFormatPickerClose();
    }
  }, [props.forceOpenFormatPicker, props.onFormatPickerClose]);

  const handleFormatAsTheDefaultDropdownClose = React.useCallback(() => {
    // TODO: Implement logic on BE integration
  }, []);

  const handleFormatAsTheDefaultDropdownChange = React.useCallback(
    (format: FormatFieldItem) => {
      handleFormatAsTheDefaultDropdownClose();
    },
    [handleFormatAsTheDefaultDropdownClose]
  );

  const {
    folders,
    folderFormats,
    isFoldersLoading,
    selectedFolder,
    selectedFormat,
    handleSelectedFolderChange,
    handleFormatSelect,
    isFolderFormatsLoading,
    recentlyUsedFormats,
    sessionFormats,
    isRecentlyUsedLoading,
  } = useDatasheetFormatManagementController({
    getFolders: props.getFoldersForDatasheet,
    getFormats: props.getDatasheetFolderFormats,
    onSubmit: (payload) => {
      handleDatasheetFormatsManagementClose();
      props.onDatasheetFormatsSubmit(payload);
      handleDatasheetFormatsManagementClose();
    },
    showErrorMessage,
    context: props.context ?? 1,
    pickerOpened: datasheetFormatsManagementOpened,
    initialSelectedFormat: props.initialSelectedFormat,
    reloadRecentlyUsedKey: props.reloadRecentlyUsedKey,
  });

  const getMenuPortalStyles = React.useCallback((css: React.CSSProperties) => ({ ...css, zIndex: 9999 }), []);

  const getMenuStyles = React.useCallback(
    (css: React.CSSProperties) => ({
      ...css,
      width: undefined,
      left: "unset",
      right: 0,
    }),
    []
  );

  const getCurrentFormatSelectMenuListComponent = React.useCallback(
    (menuProps: MenuListComponentProps<{}>) => {
      const isIdsContext = props.context === 1;
      const isPdsContext = props.context === 2;
      // Match header behaviour: in regular IDS views show "Individual", but
      // in pedigree split view (formatOnly=true even though context=1) show "Pedigree".
      const pedigreeLabel =
        isIdsContext && !props.formatOnly
          ? "Set Current Format as Default for this Individual"
          : "Set Current Format as Default for this Pedigree";

      return (
        <components.MenuList
          {...menuProps}
          className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__menu"
        >
          <h6 className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__title">
            {trDts("Recently Used Datasheet Formats")}
          </h6>

          {isRecentlyUsedLoading ? (
            <div className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__loading">
              {trDts("Loading")}
            </div>
          ) : (
            <>
              {/* Top: Persisted MRU from last session (fixed until close/reopen - GWT-style) */}
              {recentlyUsedFormats.map((format) => (
                  <div key={format.id}>
                    <Button
                      onClick={() => handleFormatSelect(format)}
                      className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn"
                    >
                      {format.name}
                    </Button>
                  </div>
                ))}
            </>
          )}
          <div className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-divider" />

          {/* Case A: Template Option OFF — show only "Set as Default for this Pedigree/Individual" */}
          {selectedFormat?.id && (isIdsContext || isPdsContext) && !props.templateOptionOn && props.onSetPedigreeDefaultFormat && (
            <Button
              onClick={() => {
                handleFormatAsTheDefaultDropdownChange(selectedFormat);
                const maybePromise = (props.onSetPedigreeDefaultFormat!(
                  Number(selectedFormat.id)
                ) as unknown) as Promise<void> | void;
                if (maybePromise && typeof (maybePromise as any).then === "function") {
                  (maybePromise as Promise<void>).finally(() => {
                    handleDatasheetFormatsManagementClose();
                  });
                } else {
                  handleDatasheetFormatsManagementClose();
                }
              }}
              className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn"
            >
              {pedigreeLabel}
            </Button>
          )}

          {/* Case B: Template Option ON — show only "Set as Default for this Folder" (disabled for non-admins) */}
          {selectedFormat?.id && (selectedFolder?.id ?? props.rootFolderId) && props.templateOptionOn && (
            <Button
              onClick={() => {
                handleFormatAsTheDefaultDropdownChange(selectedFormat);
                const effectiveFolderId = (selectedFolder?.id ?? props.rootFolderId) as number;
                if (props.onSetFolderDefaultFormat && props.canSetFolderDefault && effectiveFolderId) {
                  const maybePromise = (props.onSetFolderDefaultFormat({
                    folderId: effectiveFolderId,
                    formatId: Number(selectedFormat.id),
                  }) as unknown) as Promise<void> | void;
                  if (maybePromise && typeof (maybePromise as any).then === "function") {
                    (maybePromise as Promise<void>).finally(() => {
                      handleDatasheetFormatsManagementClose();
                    });
                  } else {
                    handleDatasheetFormatsManagementClose();
                  }
                } else {
                  handleDatasheetFormatsManagementClose();
                }
              }}
              disabled={!props.canSetFolderDefault}
              className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn--folder-default"
            >
              {"Set Current Format as Default for this Folder"}
            </Button>
          )}
          {/* Bottom: Newly selected this session (GWT-style: no divider after Set Default) */}
          {sessionFormats.map((format) => (
            <div key={`session-${format.id}`}>
              <Button
                onClick={() => handleFormatSelect(format)}
                className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn"
              >
                {format.name}
              </Button>
            </div>
          ))}
        </components.MenuList>
      );
    },
    [
      selectedFormat?.id,
      selectedFolder?.id,
      props.rootFolderId,
      handleFormatAsTheDefaultDropdownChange,
      handleFormatSelect,
      props.onSetFolderDefaultFormat,
      props.onSetPedigreeDefaultFormat,
      props.canSetFolderDefault,
      props.context,
      props.templateOptionOn,
      recentlyUsedFormats,
      sessionFormats,
      isRecentlyUsedLoading,
    ]
  );

  const getMenuListComponent = (props: MenuListComponentProps<{}>) => {
    return (
      <components.MenuList {...props} className="DatasheetsResultHeader__formats__menu">
        <ClickAwayListener onClickAway={handleFormatAsTheDefaultDropdownClose}>
          <Dropdown
            className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown"
            fullWidth
            searchable={false}
            selectedValue={selectedFormat?.name || DROPDOWN_NO_VALUE}
            menuListComponent={getCurrentFormatSelectMenuListComponent}
            onBlur={handleFormatAsTheDefaultDropdownClose}
            onMenuClose={handleFormatAsTheDefaultDropdownClose}
          />
        </ClickAwayListener>
        <FormatFieldSelector
          folders={folders as Array<FolderPrimaryListDto>}
          selectedFolder={selectedFolder as FolderPrimaryListDto}
          isFoldersLoading={isFoldersLoading}
          selectedFormat={selectedFormat}
          onSelectedFolderChange={handleSelectedFolderChange}
          formats={folderFormats}
          isFormatsLoading={isFolderFormatsLoading}
          onSelectedFormatChange={handleFormatSelect}
          moduleName={DATASHEETS_MODULE_NAME.result}
          showTableHeader={false}
          classes={{
            folders: "DatasheetsResultHeader__format-management__folders",
            formats: "DatasheetsResultHeader__format-management__formats",
          }}
          tableColumns={[
            {
              label: trDts("Format Name"),
              field: FormatColumnKey.name,
            },
          ]}
        />
      </components.MenuList>
    );
  };

  const getControlComponent = React.useCallback(
    ({ cx, children, ...rest }: ControlProps<{}>) => (
      <components.Control cx={() => {}} {...rest} className="DatasheetsResultHeader__formats__menu-control">
        <Button
          variant="text"
          className="DatasheetsResultHeader__formats-btn"
          title={trDts("Select a New Datasheet Format")}
          disabled={Boolean(props.interactionLocked) && !props.forceOpenFormatPicker}
          onClick={
            datasheetFormatsManagementOpened
              ? handleDatasheetFormatsManagementClose
              : handleDatasheetFormatsManagementOpen
          }
        >
          <i className="fa fa-edit" />
        </Button>
      </components.Control>
    ),
    [
      datasheetFormatsManagementOpened,
      handleDatasheetFormatsManagementClose,
      handleDatasheetFormatsManagementOpen,
      props.interactionLocked,
      props.forceOpenFormatPicker,
    ]
  );

  const menuIsOpen =
    datasheetFormatsManagementOpened &&
    (!props.interactionLocked || Boolean(props.forceOpenFormatPicker));

  return (
    <ClickAwayListener onClickAway={handleDatasheetFormatsManagementClose}>
      <Dropdown
        className="DatasheetsResultHeader__formats-dropdown"
        fullWidth
        searchable={false}
        menuIsOpen={menuIsOpen}
        menuListComponent={getMenuListComponent}
        controlComponent={getControlComponent}
        styles={{
          width: "100%",
          menu: getMenuStyles,
          menuPortal: getMenuPortalStyles,
        }}
        onBlur={handleDatasheetFormatsManagementClose}
        onMenuClose={handleDatasheetFormatsManagementClose}
        // External open support: when parent requests it, open/close is handled via state in the controller
        menuPortalTarget={undefined}
      />
    </ClickAwayListener>
  );
};

export const Tabs: React.FC<TabsProps> = (props) => {
  const { items, className, value, onChange, classes, onTabOrderChange, formatManagement } = props;
  const alignItems = formatManagement ? "flex-start" : "flex-end";

  const [showLeftButton, setShowLeftButton] = React.useState(false);
  const [showRightButton, setShowRightButton] = React.useState(false);
  const sliderRef = React.useRef<HTMLDivElement>(null);
  const scrollAccumulatorRef = React.useRef(0);
  const rafRef = React.useRef<number | null>(null);

  const horizontalSlidePx = 100;

  const checkButtonsVisibility = () => {
    const slider = sliderRef.current;
    if (slider) {
      setShowLeftButton(slider.scrollLeft > 0);
      setShowRightButton(slider.scrollWidth > (slider.clientWidth + Math.ceil(slider.scrollLeft)));
    }
  };

  const sliderBtnAttrKey = "data-slider-btn";
  const sliderLeftBtnAttrValue = "left";
  const sliderRightBtnAttrValue = "right";

  const setSliderBtnAttr = React.useCallback(
    (position: typeof sliderLeftBtnAttrValue | typeof sliderRightBtnAttrValue) => {
      return {
        [sliderBtnAttrKey]: position,
      };
    },
    []
  );

  React.useEffect(() => {
    checkButtonsVisibility();
    window.addEventListener("resize", checkButtonsVisibility);
    return () => window.removeEventListener("resize", checkButtonsVisibility);
  }, [items]);

  React.useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const animateScroll = () => {
      const remaining = scrollAccumulatorRef.current;
      if (Math.abs(remaining) < 0.5) {
        scrollAccumulatorRef.current = 0;
        rafRef.current = null;
        return;
      }
      const step = remaining * 0.15;
      slider.scrollLeft += step;
      scrollAccumulatorRef.current -= step;
      checkButtonsVisibility();
      rafRef.current = requestAnimationFrame(animateScroll);
    };

    const handleWheel = (e: WheelEvent) => {
      if (slider.scrollWidth > slider.clientWidth) {
        e.preventDefault();
        const normalizedDelta = Math.sign(e.deltaY) * Math.min(Math.abs(e.deltaY), 80);
        scrollAccumulatorRef.current += normalizedDelta;
        if (rafRef.current === null) {
          rafRef.current = requestAnimationFrame(animateScroll);
        }
      }
    };

    slider.addEventListener("wheel", handleWheel, { passive: false });
    slider.addEventListener("scroll", checkButtonsVisibility);
    return () => {
      slider.removeEventListener("wheel", handleWheel);
      slider.removeEventListener("scroll", checkButtonsVisibility);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  const scrollLeft = React.useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -horizontalSlidePx, behavior: "smooth" });
    }
  }, []);

  const scrollRight = React.useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: horizontalSlidePx, behavior: "smooth" });
    }
  }, []);

  const handleClickTab = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      // Use currentTarget so we always get the tab div with data-custom-tab-id,
      // not a child element (e.target can be a child and lack the data attribute).
      const tabEl = e.currentTarget as HTMLElement;
      tabEl.scrollIntoView({ behavior: "smooth" });
      const id = tabEl.dataset.customTabId;
      if (id !== undefined && id !== "") {
        onChange(id as TabsItem["id"]);
      }
    },
    [onChange]
  );

  const renderItem = React.useCallback(
    (tab: DraggableListDataItem) => {
      return (
        <div
          className={cx("DatasheetsEditorDatasheetTabs__tab", {
            ["DatasheetsEditorDatasheetTabs__tab--active"]: value == tab.id,
          })}
          key={tab.id}
          onClick={handleClickTab}
          data-custom-tab-id={tab.id}
        >
          {tab.name}
        </div>
      );
    },
    [handleClickTab, value]
  );

  const handleSelectDraggingTab = (list: Array<TabsItem>, tab: TabsItem) => {
    onTabOrderChange(list);

    // Fix: Check if tab exists before accessing its properties
    if (!tab) {
      return;
    }

    const selectMovedTab = value !== tab.id;

    if (selectMovedTab) {
      onChange(tab.id);
    }

    const someGapForTriggerSlider = 200;

    const movedTabLeftPosition =
      document.querySelector(`[data-custom-tab-id="${tab.id}"]`)?.getBoundingClientRect().left || 0;

    const movedTabRightPosition =
      movedTabLeftPosition +
      (document.querySelector(`[data-custom-tab-id="${tab.id}"]`)?.getBoundingClientRect().width || 0);

    const sliderLeftBtnPosition =
      document.querySelector(`[${sliderBtnAttrKey}="${sliderLeftBtnAttrValue}"]`)?.getBoundingClientRect().left || 0;

    const sliderRightBtnPosition =
      document.querySelector(`[${sliderBtnAttrKey}="${sliderRightBtnAttrValue}"]`)?.getBoundingClientRect().left || 0;

    if (movedTabRightPosition + someGapForTriggerSlider >= sliderRightBtnPosition) {
      scrollRight();
    } else if (sliderLeftBtnPosition + someGapForTriggerSlider >= movedTabLeftPosition) {
      scrollLeft();
    }
  };

  return (
    <Flex
      className={cx("DatasheetsEditorDatasheetTabs", className)}
      width="100%"
      justify="space-between"
      alignItems={alignItems}
    >
      <Button
        className={cx("DatasheetsEditorDatasheetTabs__slider-button", {
          ["DatasheetsEditorDatasheetTabs__slider-button--active"]: showLeftButton,
        })}
        disabled={!showLeftButton}
        onClick={scrollLeft}
        {...setSliderBtnAttr(sliderLeftBtnAttrValue)}
      >
        <i className="fa fa-chevron-left"></i>
      </Button>
      <div className="DatasheetsEditorDatasheetTabs__slider" ref={sliderRef}>
        <DraggableList
          className={cx("DatasheetsEditorDatasheetTabs__slider__items", classes?.tabs)}
          classes={{
            item: "DatasheetsEditorDatasheetTabs__slider__item",
          }}
          items={items}
          renderItem={renderItem as DraggableListProps["renderItem"]}
          onChange={handleSelectDraggingTab as DraggableListProps["onChange"]}
          direction={DraggableListDirection.horizontal}
        />
      </div>
      <Button
        className={cx("DatasheetsEditorDatasheetTabs__slider-button", {
          ["DatasheetsEditorDatasheetTabs__slider-button--active"]: showRightButton,
        })}
        disabled={!showRightButton}
        onClick={scrollRight}
        {...setSliderBtnAttr(sliderRightBtnAttrValue)}
      >
        <i className="fa fa-chevron-right"></i>
      </Button>
      {formatManagement && (
        <div className="DatasheetsEditorDatasheetTabs__formatBtn">
          <FormatManagementDropdown {...formatManagement} />
        </div>
      )}
    </Flex>
  );
};
